import { Injectable, inject } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { SiaMenuApi } from '../apis/sia-menu.api';
import { SiaComponentInfo } from '../interfaces/sia-component-info';
import { SiaMenu } from '../interfaces/sia-menu';
import { SiaWindowSize } from '../interfaces/sia-window-size';
import { SiaCacheDatabaseService } from './cache-database.service';
import { jsonDeepClone } from './jsonDeepClone';
import { SiaSignalrService } from './signalr.service';
import { SiaSizeDetectorService } from './size-detector.service';

@Injectable({
  providedIn: 'root',
})
export class SiaMenuService {
  private api = inject(SiaMenuApi);
  private cacheDatabaseService = inject(SiaCacheDatabaseService);
  private siaSizeDetectorService = inject(SiaSizeDetectorService);
  private siaSignalRService = inject(SiaSignalrService);

  private _dotUpdated: Subject<{ id: string; value: boolean }> = new Subject<{
    id: string;
    value: boolean;
  }>();
  private _subs: Subscription[] = [];
  private size: SiaWindowSize = {};
  menusDeviceType: string = '';
  private _menuUpdated: Subject<boolean> = new Subject<boolean>();
  #userMenusPromise?: Promise<SiaMenu[]>;

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    let s1 = this.siaSizeDetectorService.onResize$.subscribe((size: any) => {
      if (!size.deviceType) return;
      if (!this.size.deviceType || !size) {
        this.size = jsonDeepClone(size);
        return;
      }
      if (size.deviceType !== this.size.deviceType) {
        this.size = jsonDeepClone(size);
        this.reloadUserMenus();
      }
    });
    this._subs.push(s1);
    let s2 = this.siaSignalRService.refreshMessage$.subscribe((message) => {
      if (
        message['sidebar'] ||
        message['menu'] ||
        message['layout'] ||
        message['component'] ||
        message['*']
      ) {
        this.reloadUserMenus();
      }
    });
    this._subs.push(s2);
  }

  get dotUpdated$() {
    return this._dotUpdated.asObservable();
  }

  get menuUpdated$() {
    return this._menuUpdated.asObservable();
  }

  //#region private methods

  private async getMenuByPath(relativePath: string): Promise<SiaMenu> {
    const menus = await this.getUserMenus();
    if (menus) {
      let res = menus.filter((x) => x.relativePath === relativePath);
      if (res && res.length == 1) {
        return res[0];
      }
    }
    return {};
  }
  //#endregion

  //#region public methods

  //OK CACHE
  async reloadUserMenus() {
    // Svuota la cache prima di invalidare il riferimento di dedup,
    // così ogni chiamata concorrente a getUserMenus() troverà la cache vuota
    // e andrà direttamente all'API senza leggere dati stale.
    await this.cacheDatabaseService.clearAllUserMenu();
    this.#userMenusPromise = undefined;
    await this.getUserMenus();
    this._menuUpdated.next(true);
  }

  hasCountDot(item: { id: string; value: boolean }) {
    this._dotUpdated.next(item);
  }

  getUserMenus(): Promise<SiaMenu[]> {
    if (this.#userMenusPromise) {
      return this.#userMenusPromise;
    }

    let promiseRef: Promise<SiaMenu[]> | null = null;

    const loadMenus = async (): Promise<SiaMenu[]> => {
      try {
        const done = await this.cacheDatabaseService.menuLoaded();
        if (done) {
          return await this.cacheDatabaseService.getUserMenus();
        }

        this.menusDeviceType = this.siaSizeDetectorService.currentSize.isMobile
          ? 'mobile'
          : 'desktop';
        const menus = await this.api.getUserMenus(this.menusDeviceType);
        if (menus) {
          // Only write to cache if this is still the active request.
          if (this.#userMenusPromise === promiseRef) {
            await this.api.deleteMenuCache();
            await this.cacheDatabaseService.setUserMenus(menus);
          }
          return menus;
        }

        return menus ?? [];
      } finally {
        if (this.#userMenusPromise === promiseRef) {
          this.#userMenusPromise = undefined;
        }
      }
    };

    promiseRef = loadMenus();
    this.#userMenusPromise = promiseRef;
    return promiseRef;
  }

  async getMenuId(relativePath: string): Promise<string | null> {
    const m = await this.getMenuByPath(relativePath);
    return m?.id ?? null;
  }

  async getMenu(menuId?: string): Promise<SiaMenu | null> {
    this.menusDeviceType = this.siaSizeDetectorService.currentSize.isMobile
      ? 'mobile'
      : 'desktop';
    if (!menuId) {
      return null;
    }
    const menu = await this.cacheDatabaseService.getUserMenu(menuId);
    if (menu) {
      return menu;
    }
    return await this.api.getMenu(menuId, this.menusDeviceType);
  }
  async getMenus(): Promise<SiaMenu[] | null> {
    return await this.api.getMenus();
  }

  async getComponentInformation(componentId: string): Promise<SiaComponentInfo | null> {
    return await this.api.getComponentInformation(componentId);
  }

  // OK CACHE
  async getSideBarMenus(): Promise<SiaMenu[]> {
    const menus = await this.getUserMenus();
    if (menus) {
      const sidebarMenus = menus.filter((x) => x.sidebar);
      const favoriteNotSidebar = menus.filter(
        (x) => x.isFavorite && !x.sidebar,
      );
      return sidebarMenus.concat(favoriteNotSidebar);
    }
    return [];
  }

  async getAllSideBarMenus(): Promise<SiaMenu[]> {
    const menus = await this.getMenus();
    if (menus) {
      return menus.filter((x) => x.sidebar);
    }
    return [];
  }

  // OK CACHE
  async getSubMenus(parentId: string): Promise<SiaMenu[]> {
    const menus = await this.getUserMenus();
    if (menus) {
      const filteredMenus = menus.filter((x) => x.parentId === parentId);
      return filteredMenus.sort((a, z) => {
        if (!a.subOrderIndex) {
          return 1;
        }
        if (!z.subOrderIndex) {
          return -1;
        }
        return a.subOrderIndex - z.subOrderIndex;
      });
    }
    return [];
  }
  // OK CACHE
  async clearUserMenus() {
    await this.cacheDatabaseService.clearAllUserMenu();
    return true;
  }

  // OK CACHE
  async countSubMenu(id: string): Promise<number> {
    const menus = await this.getUserMenus();
    const items = menus?.filter((x) => x.parentId == id);
    return items?.length ?? 0;
  }

  // OK
  async updateSubMenusIndex(subMenus: SiaMenu[]): Promise<boolean> {
    return await this.api.updateSubMenusIndex(subMenus);
  }
  async updateSidebarMenusIndex(subMenus: SiaMenu[]): Promise<boolean> {
    return await this.api.updateSidebarMenusIndex(subMenus);
  }

  async hasFilterPanel(menuId: string) {
    const menu = await this.api.getMenu(menuId);
    return !!menu?.filterPanelId;
  }

  async getLinkTotalCount(
    deviceType: string,
    componentId: string,
    params: any[],
  ) {
    return await this.api.getLinkTotalCount(deviceType, componentId, params);
  }

  async saveFavorite(menuId: string, isFavorite: boolean): Promise<boolean> {
    return await this.api.saveFavorite(menuId, isFavorite);
  }

  ngOnDestroy() {
    this._subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
  //#endregion
}
